'use client'

import AddressManager, {
    EditableAddress,
} from '@/components/address/address-manager'
import { PayoutScheduleSection } from '@/components/payout-schedule/PayoutScheduleSection'
import { getAddresses, syncAddresses, toEditable } from '@/lib/api/addresses'
import { PersonType, PartnerPayoutScheduleType } from '@/lib/api/common/interfaces/partner.interface'
import { createPartner, getPartner, updatePartner } from '@/lib/api/partners/partners-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import { FaWhatsapp } from 'react-icons/fa'
import {
    MdArrowBack,
    MdBusiness,
    MdContactPhone,
    MdEmail,
    MdLocationOn,
    MdNotes,
    MdPerson,
    MdPhone,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

interface FormData {
    name: string
    tradingName: string
    email: string
    phone: string
    personType: PersonType
    cpf: string
    cnpj: string
    rg: string
    rgIssuer: string
    stateRegistration: string
    municipalRegistration: string
    street: string
    number: string
    complement: string
    neighborhood: string
    city: string
    cityCode: string
    state: string
    zipCode: string
    country: string
    bankCode: string
    bankName: string
    bankAgency: string
    bankAccount: string
    bankAccountDigit: string
    bankAccountType: string
    pixKey: string
    pixKeyType: string
    contactName: string
    contactPhone: string
    contactEmail: string
    website: string
    notes: string
    active: boolean
    whatsappNumber: string
    payoutScheduleType: PartnerPayoutScheduleType
    payoutFixedDay: string
    payoutDaysAfterAppointment: string
}

const initialFormData: FormData = {
    name: '',
    tradingName: '',
    email: '',
    phone: '',
    personType: 'JURIDICA',
    cpf: '',
    cnpj: '',
    rg: '',
    rgIssuer: '',
    stateRegistration: '',
    municipalRegistration: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    cityCode: '',
    state: '',
    zipCode: '',
    country: 'BR',
    bankCode: '',
    bankName: '',
    bankAgency: '',
    bankAccount: '',
    bankAccountDigit: '',
    bankAccountType: 'CONTA_CORRENTE',
    pixKey: '',
    pixKeyType: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    website: '',
    notes: '',
    active: true,
    whatsappNumber: '',
    payoutScheduleType: 'OPEN',
    payoutFixedDay: '',
    payoutDaysAfterAppointment: '',
}

const formatCPF = (value: string): string => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 11) {
        return numbers
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    }
    return value
}

const formatCNPJ = (value: string): string => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 14) {
        return numbers
            .replace(/^(\d{2})(\d)/, '$1.$2')
            .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
            .replace(/\.(\d{3})(\d)/, '.$1/$2')
            .replace(/(\d{4})(\d)/, '$1-$2')
    }
    return value
}

const formatPhone = (value: string): string => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 11) {
        if (numbers.length <= 10) {
            return numbers
                .replace(/^(\d{2})(\d)/, '($1) $2')
                .replace(/(\d{4})(\d)/, '$1-$2')
        }
        return numbers
            .replace(/^(\d{2})(\d)/, '($1) $2')
            .replace(/(\d{5})(\d)/, '$1-$2')
    }
    return value
}

const formatCEP = (value: string): string => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 8) {
        return numbers.replace(/^(\d{5})(\d)/, '$1-$2')
    }
    return value
}

const unMaskValue = (value: string): string => {
    return value.replace(/\D/g, '')
}

export default function PartnersFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id
    const [loading, setLoading] = useState(false)
    const [loadingPartner, setLoadingPartner] = useState(false)
    const [formData, setFormData] = useState<FormData>(initialFormData)
    /** Unidades do parceiro. Persistidas no submit, via `syncAddresses`. */
    const [addresses, setAddresses] = useState<EditableAddress[]>([])

    useEffect(() => {
        if (isEditing && id) {
            loadPartner()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEditing])

    const loadPartner = async () => {
        if (!id) return

        setLoadingPartner(true)
        try {
            const response = await getPartner(parseInt(id, 10))
            if (response.success && response.result) {
                const partner = response.result
                setFormData({
                    name: partner.name || '',
                    tradingName: partner.tradingName || '',
                    email: partner.email || '',
                    phone: partner.phone ? formatPhone(partner.phone) : '',
                    personType: partner.personType || 'JURIDICA',
                    cpf: partner.cpf ? formatCPF(partner.cpf) : '',
                    cnpj: partner.cnpj ? formatCNPJ(partner.cnpj) : '',
                    rg: partner.rg || '',
                    rgIssuer: partner.rgIssuer || '',
                    stateRegistration: partner.stateRegistration || '',
                    municipalRegistration: partner.municipalRegistration || '',
                    street: partner.street || '',
                    number: partner.number || '',
                    complement: partner.complement || '',
                    neighborhood: partner.neighborhood || '',
                    city: partner.city || '',
                    cityCode: (partner as any).cityCode || '',
                    state: partner.state || '',
                    zipCode: partner.zipCode ? formatCEP(partner.zipCode) : '',
                    country: partner.country || 'BR',
                    bankCode: partner.bankCode || '',
                    bankName: partner.bankName || '',
                    bankAgency: partner.bankAgency || '',
                    bankAccount: partner.bankAccount || '',
                    bankAccountDigit: partner.bankAccountDigit || '',
                    bankAccountType:
                        (partner as any).bankAccountType || 'CONTA_CORRENTE',
                    pixKey: partner.pixKey || '',
                    pixKeyType: partner.pixKeyType || '',
                    contactName: partner.contactName || '',
                    contactPhone: partner.contactPhone ? formatPhone(partner.contactPhone) : '',
                    contactEmail: partner.contactEmail || '',
                    website: partner.website || '',
                    notes: partner.notes || '',
                    active: partner.active ?? true,
                    whatsappNumber: partner.whatsappNumber ? formatPhone(partner.whatsappNumber) : '',
                    payoutScheduleType: partner.payoutScheduleType || 'OPEN',
                    payoutFixedDay: partner.payoutFixedDay ? String(partner.payoutFixedDay) : '',
                    payoutDaysAfterAppointment: partner.payoutDaysAfterAppointment
                        ? String(partner.payoutDaysAfterAppointment)
                        : '',
                })

                // Endereços vivem em tabela própria. Parceiro migrado tem o
                // legado como principal; se ainda não migrou, a lista vem vazia
                // e o bloco antigo (abaixo) segue mostrando o endereço único.
                const addressResponse = await getAddresses({
                    partnerId: parseInt(id!, 10),
                })
                if (addressResponse.success) {
                    setAddresses(toEditable(addressResponse.result))
                }
            } else {
                toast.error('Parceiro não encontrado')
                navigate('/person/partners')
            }
        } catch (error) {
            toast.error('Erro ao carregar parceiro')
            navigate('/person/partners')
        } finally {
            setLoadingPartner(false)
        }
    }

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        if (!formData.name.trim()) {
            toast.warning('Preencha o nome')
            return
        }

        if (!formData.email) {
            toast.warning('Preencha o email')
            return
        }

        if (formData.payoutScheduleType === 'FIXED_DAY') {
            const day = parseInt(formData.payoutFixedDay, 10)
            if (!day || day < 1 || day > 31) {
                toast.warning('Informe um dia do mês válido (1 a 31) para o repasse')
                return
            }
        }

        if (formData.payoutScheduleType === 'DAYS_AFTER_APPOINTMENT') {
            const days = parseInt(formData.payoutDaysAfterAppointment, 10)
            if (!days || days < 1) {
                toast.warning('Informe a quantidade de dias após o agendamento para o repasse')
                return
            }
        }

        setLoading(true)

        try {
            const dataToSend = {
                name: formData.name.trim(),
                tradingName: formData.tradingName.trim() || undefined,
                email: formData.email.trim() || undefined,
                phone: unMaskValue(formData.phone) || undefined,
                personType: formData.personType,
                cpf: unMaskValue(formData.cpf) || undefined,
                cnpj: unMaskValue(formData.cnpj) || undefined,
                rg: formData.rg.trim() || undefined,
                rgIssuer: formData.rgIssuer.trim() || undefined,
                stateRegistration: formData.stateRegistration.trim() || undefined,
                municipalRegistration: formData.municipalRegistration.trim() || undefined,
                // Endereço não vai mais aqui: as colunas do parceiro viraram
                // legado somente-leitura e os endereços são gravados em tabela
                // própria por `syncAddresses`, depois que o parceiro existe.
                // Manter o envio faria as duas fontes divergirem em silêncio.
                bankCode: formData.bankCode.trim() || undefined,
                bankName: formData.bankName.trim() || undefined,
                bankAgency: formData.bankAgency.trim() || undefined,
                bankAccount: formData.bankAccount.trim() || undefined,
                bankAccountDigit: formData.bankAccountDigit.trim() || undefined,
                bankAccountType: formData.bankAccountType || 'CONTA_CORRENTE',
                pixKey: formData.pixKey.trim() || undefined,
                pixKeyType: formData.pixKeyType.trim() || undefined,
                contactName: formData.contactName.trim() || undefined,
                contactPhone: unMaskValue(formData.contactPhone) || undefined,
                contactEmail: formData.contactEmail.trim() || undefined,
                website: formData.website.trim() || undefined,
                notes: formData.notes.trim() || undefined,
                active: formData.active,
                whatsappNumber: unMaskValue(formData.whatsappNumber) || undefined,
                companyId: user.companyId,
                payoutScheduleType: formData.payoutScheduleType,
                payoutFixedDay:
                    formData.payoutScheduleType === 'FIXED_DAY' && formData.payoutFixedDay
                        ? parseInt(formData.payoutFixedDay, 10)
                        : null,
                payoutDaysAfterAppointment:
                    formData.payoutScheduleType === 'DAYS_AFTER_APPOINTMENT' && formData.payoutDaysAfterAppointment
                        ? parseInt(formData.payoutDaysAfterAppointment, 10)
                        : null,
            }

            let response

            if (isEditing) {
                response = await updatePartner(parseInt(id!, 10), dataToSend)
            } else {
                response = await createPartner(dataToSend)
            }

            if (response.success) {
                // Endereços só podem ser gravados depois que o parceiro existe:
                // na criação o id só nasce aqui. Falha ao sincronizar não desfaz
                // o parceiro — avisa, para o operador não sair achando que as
                // unidades foram salvas.
                const partnerId = isEditing
                    ? parseInt(id!, 10)
                    : response.result?.id
                if (partnerId) {
                    try {
                        await syncAddresses({ partnerId }, addresses)
                    } catch {
                        toast.error(
                            'Parceiro salvo, mas houve erro ao salvar os endereços'
                        )
                    }
                }

                toast.success(
                    isEditing
                        ? 'Parceiro atualizado com sucesso'
                        : 'Parceiro criado com sucesso'
                )
                navigate('/person/partners')
            } else {
                toast.error(
                    response.error ||
                    (isEditing
                        ? 'Erro ao atualizar parceiro'
                        : 'Erro ao criar parceiro')
                )
            }
        } catch (error) {
            toast.error(
                isEditing
                    ? 'Erro ao atualizar parceiro'
                    : 'Erro ao criar parceiro'
            )
        } finally {
            setLoading(false)
        }
    }

    // A busca de CEP local saiu junto com o bloco de endereço único: agora ela
    // mora em `AddressFields`, que vai pelo proxy do backend em vez de chamar a
    // ViaCEP direto do navegador.

    if (loadingPartner) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando parceiro...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/person/partners')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdBusiness className="text-2xl text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-base-content">
                        {isEditing ? 'Editar Parceiro' : 'Novo Parceiro'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-8">
                {/* Dados Basicos */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                        <MdPerson className="text-xl" />
                        Dados Básicos
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Nome <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                name="name"
                                placeholder="Nome completo ou razão social"
                                className="input input-bordered w-full"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Email <span className="text-error">*</span></span>
                            </label>
                            <div className="relative">
                                <MdEmail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="email@exemplo.com"
                                    className="input input-bordered w-full pl-10"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Tipo de Pessoa</span>
                            </label>
                            <select
                                name="personType"
                                className="select select-bordered w-full"
                                value={formData.personType}
                                onChange={handleChange}
                            >
                                <option value="JURIDICA">Pessoa Jurídica</option>
                                <option value="FISICA">Pessoa Física</option>
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    {formData.personType === 'JURIDICA' ? 'CNPJ' : 'CPF'}
                                </span>
                            </label>
                            {formData.personType === 'JURIDICA' ? (
                                <input
                                    type="text"
                                    name="cnpj"
                                    placeholder="00.000.000/0000-00"
                                    className="input input-bordered w-full"
                                    value={formData.cnpj}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            cnpj: formatCNPJ(e.target.value),
                                        }))
                                    }
                                    maxLength={18}
                                />
                            ) : (
                                <input
                                    type="text"
                                    name="cpf"
                                    placeholder="000.000.000-00"
                                    className="input input-bordered w-full"
                                    value={formData.cpf}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            cpf: formatCPF(e.target.value),
                                        }))
                                    }
                                    maxLength={14}
                                />
                            )}
                        </div>

                        {formData.personType === 'FISICA' && (
                            <>
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text">RG</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="rg"
                                        placeholder="Número do RG"
                                        className="input input-bordered w-full"
                                        value={formData.rg}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text">Órgão Emissor</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="rgIssuer"
                                        placeholder="SSP/SP"
                                        className="input input-bordered w-full"
                                        value={formData.rgIssuer}
                                        onChange={handleChange}
                                    />
                                </div>
                            </>
                        )}

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Telefone</span>
                            </label>
                            <div className="relative">
                                <MdPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="(00) 00000-0000"
                                    className="input input-bordered w-full pl-10"
                                    value={formData.phone}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            phone: formatPhone(e.target.value),
                                        }))
                                    }
                                    maxLength={15}
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text flex items-center gap-2">
                                    <FaWhatsapp className="text-green-500" />
                                    WhatsApp
                                </span>
                            </label>
                            <div className="relative">
                                <FaWhatsapp className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-500 text-xl z-10 pointer-events-none" />
                                <input
                                    type="text"
                                    name="whatsappNumber"
                                    placeholder="(00) 00000-0000"
                                    className="input input-bordered w-full pl-10"
                                    value={formData.whatsappNumber}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            whatsappNumber: formatPhone(e.target.value),
                                        }))
                                    }
                                    maxLength={15}
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Website</span>
                            </label>
                            <input
                                type="url"
                                name="website"
                                placeholder="https://www.exemplo.com"
                                className="input input-bordered w-full"
                                value={formData.website}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Enderecos (multiplas unidades) */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <div className="flex items-start justify-between gap-3 mb-1">
                        <h2 className="text-lg font-semibold text-base-content flex items-center gap-2">
                            <MdLocationOn className="text-xl" />
                            Endereços
                        </h2>
                    </div>
                    <p className="text-sm text-base-content/60 mb-4">
                        Um parceiro pode atender em mais de uma unidade. Dê um
                        título a cada uma (ex.: &quot;Unidade Centro&quot;) para
                        que o operador saiba qual escolher no agendamento.
                    </p>

                    <AddressManager
                        value={addresses}
                        onChange={setAddresses}
                        disabled={loading}
                        emptyHint="Nenhum endereço cadastrado para este parceiro."
                    />
                </div>

                {/* Contato Adicional */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                        <MdContactPhone className="text-xl" />
                        Contato Adicional
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Nome do Contato</span>
                            </label>
                            <input
                                type="text"
                                name="contactName"
                                placeholder="Nome"
                                className="input input-bordered w-full"
                                value={formData.contactName}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Telefone do Contato</span>
                            </label>
                            <input
                                type="tel"
                                name="contactPhone"
                                placeholder="(00) 00000-0000"
                                className="input input-bordered w-full"
                                value={formData.contactPhone}
                                onChange={e =>
                                    setFormData(prev => ({
                                        ...prev,
                                        contactPhone: formatPhone(e.target.value),
                                    }))
                                }
                                maxLength={15}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Email do Contato</span>
                            </label>
                            <input
                                type="email"
                                name="contactEmail"
                                placeholder="email@exemplo.com"
                                className="input input-bordered w-full"
                                value={formData.contactEmail}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Observacoes */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                        <MdNotes className="text-xl" />
                        Observações
                    </h2>

                    <div className="form-control w-full">
                        <textarea
                            name="notes"
                            placeholder="Observações sobre o parceiro..."
                            className="textarea textarea-bordered w-full h-32"
                            value={formData.notes}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                {/* Repasse: quando fechar (o dia fixo liga o agrupamento automático)
                    e para onde o dinheiro vai. Componente compartilhado com os
                    cadastros de profissional e fornecedor. */}
                <PayoutScheduleSection
                    description="Defina como os valores serão repassados a este parceiro."
                    value={{
                        payoutScheduleType: formData.payoutScheduleType,
                        payoutFixedDay: formData.payoutFixedDay,
                        payoutDaysAfterAppointment:
                            formData.payoutDaysAfterAppointment,
                    }}
                    onChange={next =>
                        setFormData(prev => ({ ...prev, ...next }))
                    }
                    transfer={{
                        value: {
                            pixKey: formData.pixKey,
                            pixKeyType: formData.pixKeyType,
                            bankCode: formData.bankCode,
                            bankAgency: formData.bankAgency,
                            bankAccount: formData.bankAccount,
                            bankAccountDigit: formData.bankAccountDigit,
                            bankAccountType: formData.bankAccountType,
                        },
                        onChange: next =>
                            setFormData(prev => ({ ...prev, ...next })),
                    }}
                />

                {/* Status */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <div className="form-control">
                        <label className="cursor-pointer label justify-start gap-4">
                            <input
                                type="checkbox"
                                name="active"
                                className="toggle toggle-secondary"
                                checked={formData.active}
                                onChange={handleChange}
                            />
                            <span className="label-text">Parceiro ativo</span>
                        </label>
                    </div>
                </div>

                {/* Botoes */}
                <div className="flex gap-2 pt-4">
                    <button
                        type="button"
                        className="btn btn-ghost flex-1"
                        onClick={() => navigate('/person/partners')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary flex-1"
                        disabled={loading || !formData.name.trim()}
                    >
                        {loading ? (
                            <span className="loading loading-spinner"></span>
                        ) : isEditing ? (
                            'Atualizar'
                        ) : (
                            'Salvar'
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
